'use client';

import { Button } from '@common/components';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

//* 이 날짜가 지나면 노출이 멈춘다. 종료 후 이 컴포넌트와 layout 마운트를 함께 제거한다.
const DISPLAY_END_DATE = '2026-08-21';

const TITLE = '🎁 혜택탭이 새로워졌어요!';

//* 이모지는 타이틀 1개만 둔다 — 섹션 제목의 🎫는 14px에서 판독 불가한 덩어리로 렌더되고,
//* 스크린리더가 "입장권 행운권"처럼 유사 명사를 겹쳐 읽는다.
const NOTICE_SECTIONS = [
  {
    title: '행운권',
    description: '열어보면 웰포인트를 받을 수 있어요.',
  },
  {
    title: '행운권 로또',
    description:
      '행운권을 받을 때마다 응모권과 상금이 쌓여요.\n응모권 5장을 모으고 목표금액에 도달하면 응모할 수 있고, 당첨되면 쌓인 상금을 모두 받을 수 있어요!',
  },
  {
    title: '새로워진 혜택도 확인해보세요',
    description:
      '출석체크 보상도 이제 행운권으로 지급돼요.\n혜택 상점에 네이버페이 포인트, GS25, 다이소 1천원권이 새롭게 추가됐어요.',
  },
];

const getIsDisplayable = () => {
  if (dayjs().isAfter(DISPLAY_END_DATE, 'day')) {
    return false;
  }

  //* 1회 노출 후 종료. 날짜로 비교하면 종료일까지 매일 다시 떠서 끌 수단이 없어진다.
  try {
    return !localStorage.getItem(STORAGE_KEY.BENEFIT_OPEN_NOTICE_SHOWN);
  } catch {
    //* 저장소가 막힌 웹뷰에서는 노출 기록을 남길 수 없어 매 진입마다 다시 뜬다 — 열지 않는다.
    return false;
  }
};

export const BenefitOpenNoticePopup = () => {
  const router = useCustomRouter();
  const isAppModalOpened = useDeviceInfoStore(
    (state) => state.appModal.isOpened,
  );
  const isDeviceInfoMounted = useDeviceInfoStore((state) => state.isMounted);
  const webviewType = useDeviceInfoStore((state) => state.webviewType);
  const appVersion = useDeviceInfoStore((state) => state.appVersion);

  const [isDisplayable, setIsDisplayable] = useState(false);

  //* localStorage 판정은 마운트 후에만 가능하다(서버 렌더 시점엔 없다).
  useEffect(() => {
    if (!getIsDisplayable()) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- 클라이언트 전용 저장소 판정 결과로 최초 노출을 결정. 파생값으로 대체 불가
    setIsDisplayable(true);
  }, []);

  //* 앱 모달(권한·강제 업데이트)에 양보한다 — 둘 다 같은 드로워로 떠서 겹치고,
  //* 그중 권한·강제 업데이트는 사용자가 닫을 수 없다.
  //* 앱에서는 디바이스 판정 + 버전 응답이 끝난 뒤에만 공지를 연다. 그렇지 않으면
  //* 공지가 먼저 열린 직후 forceUpdate가 도착해 닫힘/열림 애니메이션이 겹칠 수 있다.
  const isAppStartupResolved =
    isDeviceInfoMounted &&
    (webviewType !== WEBVIEW_TYPE.WELLO_APP || Boolean(appVersion));
  const isOpened = isDisplayable && isAppStartupResolved && !isAppModalOpened;

  //* 노출 기록은 사용자가 실제로 보고 닫은 시점에만 남긴다. 열린 즉시 남기면
  //* 앱 모달에 밀려 못 본 사용자가 공지를 잃는다.
  const close = () => {
    setIsDisplayable(false);
    try {
      localStorage.setItem(STORAGE_KEY.BENEFIT_OPEN_NOTICE_SHOWN, 'true');
    } catch {
      //* 기록에 실패해도 이번 세션 노출은 위에서 이미 끝났다. 이동(moveTo)을 막지 않는다.
    }
  };

  const moveTo = (pathname: string) => {
    close();
    router.push(pathname);
  };

  return (
    <ResponsiveDialog
      opened={isOpened}
      onClose={close}
      drawerType="bottom"
      title={TITLE}
      modalHeader={TITLE}
    >
      <article className="flex flex-col gap-16 pb-[var(--safe-area-bottom,0px)]">
        <p className="break-keep font-400 text-Gray-700 typo-body4">
          더 많은 혜택과 즐거움을 누릴 수 있도록 혜택탭이 새롭게
          업그레이드됐어요.
        </p>
        <ul className="flex flex-col gap-16 rounded-small bg-Gray-50 p-16">
          {NOTICE_SECTIONS.map(({ title, description }) => (
            <li key={title} className="flex flex-col gap-4">
              <h3 className="font-700 text-Gray-900 typo-body4">{title}</h3>
              <p className="whitespace-pre-wrap break-keep font-400 text-Gray-700 typo-body4">
                {description}
              </p>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-8">
          <Button
            dimensions="H48"
            onClick={() => moveTo(ROUTES.BENEFIT.pathname)}
          >
            혜택탭 보러가기
          </Button>
          <Button theme="sub-text" dimensions="H40" onClick={close}>
            닫기
          </Button>
        </div>
      </article>
    </ResponsiveDialog>
  );
};
